import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, effect, input, ChangeDetectionStrategy } from '@angular/core';

import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { SkSpinner3Component } from '@rocksoft/cdk';
import { filter } from 'rxjs/operators';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-command-spinner',
  templateUrl: './command-spinner.component.html',
  styleUrls: ['./command-spinner.component.scss'],
  animations: [
    trigger('progressState', [
      state('Sending', style({})),
      state('Queued', style({})),
      state('Acknowledged', style({})),
      state('Error', style({})),
      state('CommandFormatError', style({})),
      state('NotNow', style({})),
    ]),
    trigger('control-animation', [
      state('true', style({})),
      state('false', style({})),
      transition('* => true', [
        style({
          opacity: 0,
        }),
        animate(500, style({ opacity: 1 })),
      ]),
      transition('* => false', [style({ opacity: 1 }), animate(500, style({ opacity: 0 }))]),
    ]),
    trigger('text-animation', [
      transition('void => left', [
        style({
          opacity: 0,
          transform: 'translate3d(-100%, 0, 0)',
        }),
        animate(500, style({ opacity: 1, transform: 'none' })),
      ]),
      transition('left => void', [
        style({ opacity: 1, transform: 'none' }),
        animate(500, style({ opacity: 0, transform: 'translate3d(100%, 0, 0)' })),
      ]),
      transition('void => right', [
        style({
          opacity: 0,
          transform: 'translate3d(100%, 0, 0)',
        }),
        animate(500, style({ opacity: 1, transform: 'none' })),
      ]),
      transition('right => void', [
        style({ opacity: 1, transform: 'none' }),
        animate(500, style({ opacity: 0, transform: 'translate3d(-100%, 0, 0)' })),
      ]),
      transition('void => up', [
        style({
          opacity: 0,
          transform: 'translate3d(0, 100%, 0)',
        }),
        animate(500, style({ opacity: 1, transform: 'none' })),
      ]),
      transition('up => void', [
        style({ opacity: 1, transform: 'none' }),
        animate(500, style({ opacity: 0, transform: 'translate3d(0, -100%, 0)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SkSpinner3Component, AsyncPipe],
})
export class CommandSpinnerComponent implements OnDestroy {
  private subscription: Subscription;
  private _loading: Subject<boolean> = new BehaviorSubject(false);
  isLoading: Observable<boolean> = this._loading.asObservable();
  status: string;
  completed: boolean;
  error: string;

  readonly small = input(false);
  readonly direction = input('up');
  readonly reverse = input(false);

  readonly commandProgress = input.required<Observable<CommandProgress>>();

  constructor() {
    effect(() => {
      const commandProgress = this.commandProgress();
      this.cleanUp();
      this.error = '';

      if (commandProgress) {
        this.subscription = commandProgress.pipe(filter((p) => p !== null)).subscribe(
          (progress) => {
            this.status = progress.status;
            switch (progress.status) {
              case 'Sending':
              case 'Queued':
              case 'Send':
                this.completed = false;
                this._loading.next(true);
                break;
              case 'Error':
              case 'CommandFormatError':
                if (progress.errorChain && progress.errorChain.length > 0) {
                  this.error = progress.errorChain[0].usEnglishDescription;
                }
              /* falls through */
              case 'NotNow':
              case 'Acknowledged':
                this.completed = true;
                setTimeout(() => {
                  this._loading.next(false);
                }, 2000);
                break;
            }
          },
          () => {
            return;
          },
          () => {
            return;
          }
        );
      }
    });
  }

  ngOnDestroy() {
    this.cleanUp();
  }

  private cleanUp(): void {
    this.completed = false;
    // Cleanup previous subscription
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
